import { Component, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { Apollo, gql, QueryRef } from 'apollo-angular';
import { BasicResourceUpdateInput, CoreLibService, Direction, ICategory, Mutation, Query, Resource, ResourceCategory, ResourceSearchCriteria, ResourceStatus, UsageStatistics } from 'core-lib';
import { ConfirmationService, MessageService, SortEvent, ToastMessageOptions } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FileUpload } from 'primeng/fileupload';
import { TableLazyLoadEvent } from 'primeng/table';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';
import { ManagesCategories } from './manages-categories/manages-categories';
import { CategoryManagerComponent } from './manages-categories/manages-categories.component';
import { MediaUploadComponent } from './media-upload/media-upload.component';
import { Message } from 'primeng/message';

@Component({
    selector: 'lib-media-library-lib',
    templateUrl: './media-library-lib.html',
    styleUrls: ['./media-library-lib.scss'],
    standalone: false
})
export class MediaLibraryLibComponent implements OnInit, OnDestroy, ManagesCategories {

  @ViewChild(FileUpload) fileUpload: FileUpload;
  resources: Resource[];
  categories: ResourceCategory[];
  showUpload: false;
  usageStatistics: UsageStatistics;
  mimeFilterOptions = [
    { value: 'image', name: $localize`:@@media-library.filter.images.name:Image`, icon: 'far fa-file-image ', disabled: false, tooltip: $localize`:@@media-library.filter.images.toolip:Images` },
    { value: 'video', name: $localize`:@@media-library.filter.videos.name:Video`, icon: 'far fa-file-video', disabled: false, tooltip: $localize`:@@media-library.filter.videos.toolip:Videos` },
    { value: 'audio', name: $localize`:@@media-library.filter.audios.name:Audio`, icon: 'far fa-file-music', disabled: false, tooltip: $localize`:@@media-library.filter.audios.toolip:Audios` },
    { value: 'pdf', name: $localize`:@@media-library.filter.pdf.name:PDF`, icon: 'far fa-file-lines', disabled: false, tooltip: $localize`:@@media-library.filter.pdf.toolip:PDFs` }];
  filter = {
    text: null, mimeType: ['image', 'video', 'video', 'audio', 'pdf'], userUploads: false, category: null, public: null
  };

  buttonLabels = {
    expandButton: $localize`:@@media-library.filter.ExpandButton:View additional information`,
    hideButton: $localize`:@@media-library.filter.minimizeButton:Hide additional information`,
  }

  first = 0;
  pageSize = 50;
  totalRecords = 0;
  sort = { property: 'lastModificationDate', direction: Direction.Desc };
  filterTimer: any;
  selectionMode = null;
  selectionRequested = false;
  selectedResource: Resource[] | Resource;
  clonedEntries: { [s: string]: Resource } = {};

  private resourceQuerySubscription: Subscription;
  private resourceQueryRef: QueryRef<Query>;

  private convertingObjectsPollQueryRef: QueryRef<Query>;
  private convertingObjectsPollSubscription: Subscription;

  dialogControl = { context: null, showVideoEmbedDialog: false, showAudioEmbedDialog: false, editResourceDialog: false };
  firstLoad = true;
  RESOURCE_FIELDS = `id
  title
  creator
  url
  contentLength
  creationDate
  mimeType
  lastModificationDate
  status
  previewImageUrl
  resourceCategory{
    name
    color
    id
  }
  width
  height
  notes
  user_comment
  user_title
  user_contact
  user_category
  lat
  lng
  altText`;

  RESOURCE_SEARCH_QUERY = gql`
  query getResources($page: Int!,$size: Int!,$search: ResourceSearchCriteria!,$sort:Sort!){
    findResources(search : $search, page: { page : $page, size : $size, sort: $sort}) {
      totalcount
      content{
        ${this.RESOURCE_FIELDS}
      }}}`;

  FIND_RESOURCES_BY_IDS = gql`
  query findResourcesByIds($ids:[ID]!){
    findResourcesByIds(ids : $ids) {
      ${this.RESOURCE_FIELDS}
      }}`;

  USAGE_STATISTIC_QUERY = gql`
  query usageStatistics{
    usageStatistics{
      storageUsedPercentage
      storageUsedTotal
      storageQuota
    }
  }`;

  USAGE_STATISTIC_MUTATION = gql`
  mutation updateStatistics{
    updateStatistics{
      storageUsedPercentage
      storageUsedTotal
      storageQuota
    }
  }`;

  DELETE_RESOURCE_MUTATION = gql`
    mutation deleteResource($id:ID!){
      deleteResource(id: $id) {
       id
    }}`;


  DELETE_MULTIRESOURCE_MUTATION = gql`
    mutation deleteResources($ids:[ID]!){
      deleteResources(ids: $ids) {
       id
    }}`;

  UPDATE_RESOURCE_MUTATION = gql`
    mutation updateResource($id:ID!,$resource: BasicResourceUpdateInput!){
      updateResource(id: $id,resource: $resource) {
        ${this.RESOURCE_FIELDS}
    }}`;

  RESOURCE_CATEGORY_QUERY = gql`
  query FindResourceCategories($scope: String!){
    findResourceCategories(scope:$scope){
      id
      name
      scope
      color
      lastModificationDate
    }
  }`;

  UPDATE_RESOURCE_CATEGORY_MUTATION = gql`
  mutation updateResourceCategory($id:ID!,$input: ResourceCategoryInput!){
    updateResourceCategory(id: $id, input: $input) {
    id
    name
    scope
    color
    lastModificationDate
  }}`;

  CREATE_RESOURCE_CATEGORY_MUTATION = gql`
  mutation createResourceCategory($input: ResourceCategoryInput!){
    createResourceCategory(input: $input) {
    id
    name
    scope
    color
    lastModificationDate
  }}`;

  DELETE_RESOURCE_CATEGORY_MUTATION = gql`
  mutation deleteResourceCategory($id:ID!){
    deleteResourceCategory(id: $id) {
    id
    name
    scope
    color
    lastModificationDate
  }}`;

  constructor(private apollo: Apollo,
    @Optional() private dialogRef: DynamicDialogRef,
    @Optional() private dialogConfig: DynamicDialogConfig,
    @Inject("environment") public environment: any,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private coreLibService: CoreLibService
  ) {

    if (this.dialogConfig && this.dialogConfig.data) {
      if (this.dialogConfig.data.filterOptions) {
        this.filter.mimeType = this.dialogConfig.data.filterOptions;
        this.mimeFilterOptions.forEach((fo) => fo.disabled = true);
        this.mimeFilterOptions.filter((fo) => { return this.dialogConfig.data.filterOptions.indexOf(fo.value) > -1 }).forEach((fo) => fo.disabled = false);
      }
      if (this.dialogConfig.data.selectionMode) {
        this.selectionMode = this.dialogConfig.data.selectionMode;
        this.selectionRequested = true;
        this.filter.public = true;
      }
      if (this.dialogConfig.data.selectedResources) {
        this.selectedResource = this.dialogConfig.data.selectedResources;
      }
    }
  }


  ngAfterViewChecked(): void {

    if (this.firstLoad) {
      let elem = document.getElementById("addRessources") as HTMLElement;

        if (elem != null) {
        elem?.focus();

        this.firstLoad = false;
      }
    }
  }

  // umwandeln der ResourceCategories ind Categories und returnen vom Observable
  listCategories(): Observable<ICategory[]> {
    return this.apollo.query<Query>({ query: this.RESOURCE_CATEGORY_QUERY, variables: { scope: 'IMAGES' } })
      .pipe(
        map(result => result.data.findResourceCategories),
        map(result => result.map(resourceCategory => resourceCategory as ICategory))
      );
  }

  saveCategory(productId, request): Observable<ICategory> {
    return this.apollo.mutate<Mutation>({
      mutation: this.UPDATE_RESOURCE_CATEGORY_MUTATION,
      variables: { id: productId, input: request }
    }).pipe(
      map(result => result.data.updateResourceCategory as ICategory)
    );
  }

  createCategory(input: ICategory): Observable<ICategory> {
    const resourceCategory = input as ResourceCategory
    resourceCategory.scope = 'IMAGES';

    return this.apollo.mutate<Mutation>({
      mutation: this.CREATE_RESOURCE_CATEGORY_MUTATION,
      variables: { input: resourceCategory }
    }).pipe(
      map(result => result.data.createResourceCategory as ICategory)
    );
  }

  get searchCriteria(): ResourceSearchCriteria {
    return { text: this.filter.text, mimeTypes: this.filter.mimeType, userUploads: this.filter.userUploads, categoryId: this.filter.category?.id, status: this.filter.public ? [ResourceStatus.Active] : null } as ResourceSearchCriteria
  }

  deleteCategory(categoryId: string): Observable<Boolean> {
    return this.apollo.query<Mutation>({
      query: this.DELETE_RESOURCE_CATEGORY_MUTATION,
      variables: { id: categoryId }
    }).pipe(
      map(result => result.data.deleteResourceCategory != null)
    );
  }

  /**
   * toggle multi selection mode
   */
  toggleMultiSelectMode(): void {
    if (this.selectionMode == null) {
      this.selectionMode = 'multi';
    } else {
      this.selectionMode = null;
    }
  }

  copyToClipboard(event: Event, resource: Resource) {
    event.stopPropagation();
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(resource.url).then(() => {
        this.messageService.add({ severity: 'success', summary: $localize`:@@media-upload.clipboard.copy:Copied`, detail: $localize`:@@media-upload.clipboard.copyok:Copy to clipboard successfull` });
      }).catch(e => {
        // this.messageService.add({ severity: 'error', summary: $localize`:@@media-upload.clipboard.error:Error`, detail: e });
      });
    }
    const customEvent = new CustomEvent('copy-to-clipboard', { detail: { text: resource.url } });
    window.dispatchEvent(customEvent);
  }

  openQrCode(event: Event, resource: Resource) {
    event.stopPropagation();
    let qrUrl = this.environment.apiURL + '/file-api/qrcode?code=' + resource.url + '&thumbnail=false&access_token=' + this.coreLibService.getJWTToken();
    window.open(qrUrl, 'image-preview');
  }

  openPreview(event: Event, resource: Resource) {
    event.stopPropagation()
    if (resource.mimeType.indexOf('image/')) {
      window.open(resource.url, 'image-preview');
    } else if (resource.mimeType.indexOf('video/')) {
      window.open(resource.url, 'video-preview');
    } else if (resource.mimeType.indexOf('pdf')) {
      window.open(resource.url, 'pdf-preview');
    }
  }

  upload(): void {
    let accept = "*/*";
    if (this.dialogConfig && this.dialogConfig.data && this.dialogConfig.data.filterOptions) {
      accept = this.dialogConfig.data.filterOptions + "/*";
    }
    this.dialogService.open(MediaUploadComponent, {
      width: '90%',
      height: '90%',
      focusOnShow: false,
      modal: true,
      draggable: false,
      closable: true,
      closeAriaLabel:$localize`:@@media-upload.header.closeButton:Close`,
      focusTrap: true,
      data: { accept: accept },
      header: $localize`:@@media-upload.header.title:Add File`,
    }).onClose.subscribe((result) => {
      if (result && result.success) {
        const event = new CustomEvent('resource-uploaded');
        window.dispatchEvent(event);
        this.updateUsageStatistics();
        this.resourceQueryRef.refetch({
          search: this.searchCriteria,
          page: this.first / this.pageSize,
          size: this.pageSize,
          sort: { property: 'lastModificationDate', direction: Direction.Desc }
        }).then(() => {
        });
      }
    });
  }

  updateUsageStatistics(): void {
    this.apollo.mutate<Mutation>({
      mutation: this.USAGE_STATISTIC_MUTATION,
      variables: {}
    }).subscribe(result => {
      this.usageStatistics = result.data.updateStatistics;
    });
  }

  acceptSelection(): void {
    this.dialogRef.close({ resources: this.selectedResource });
  }

  ngOnDestroy(): void {
    this.resourceQuerySubscription.unsubscribe();
    this.convertingObjectsPollSubscription.unsubscribe();
  }

  openGoogleMaps(event, resource: Resource) {
    event.preventDefault();
    window.open(`https://maps.google.com/?q=${resource.lat},${resource.lng}`, 'geo');
  }

  ngOnInit(): void {
    this.resourceQueryRef = this.apollo.watchQuery<Query>({
      query: this.RESOURCE_SEARCH_QUERY,
      variables: {
        page: this.first / this.pageSize,
        size: this.pageSize,
        search: this.searchCriteria,
        sort: this.sort
      }
    });
    this.resourceQuerySubscription = this.resourceQueryRef.valueChanges.subscribe((result) => {
      this.resources = result.data.findResources.content;
      this.totalRecords = result.data.findResources.totalcount;
      const objectsToObserve = this.resources.filter((r) => r.status === ResourceStatus.Converting).map((r) => r.id);
      if (objectsToObserve.length > 0) {
        this.convertingObjectsPollQueryRef.setVariables({ ids: objectsToObserve });
        this.convertingObjectsPollQueryRef.startPolling(5000);
      } else {
        this.convertingObjectsPollQueryRef.stopPolling();
      }
    });
    this.loadResourceCategories();
    this.convertingObjectsPollQueryRef = this.apollo.watchQuery<Query>({
      query: this.FIND_RESOURCES_BY_IDS,
      variables: {
        ids: []
      },
      pollInterval: 0
    });
    this.convertingObjectsPollSubscription = this.convertingObjectsPollQueryRef.valueChanges.subscribe((result) => {
      if (!result.errors) {
        for (const updatedResource of result.data.findResourcesByIds) {
          const index = this.resources.findIndex((r) => r.id === updatedResource.id);
          if (index > -1) {
            if (this.resources[index].status !== updatedResource.status) {
              this.resources[index] = updatedResource;
              this.resources = [...this.resources];
            }
          }
        }
      }
    });

    this.apollo.query<Query>({ query: this.USAGE_STATISTIC_QUERY, variables: {} }).subscribe((result) => {
      this.usageStatistics = result.data.usageStatistics;
    });
  }

  /**
   * refreshs resource categories from backend
   */
  loadResourceCategories(): void {
    this.apollo.query<Query>({ query: this.RESOURCE_CATEGORY_QUERY, variables: { scope: 'IMAGES' } }).subscribe((result) => {
      this.categories = result.data.findResourceCategories;
    });
  }

  onSort(event: SortEvent): void {
    this.sort = { direction: (event.order === -1 ? Direction.Desc : Direction.Asc), property: event.field };
  }

  loadResources(event: TableLazyLoadEvent): void {
    setTimeout(() => {
      const currentPage = event.first / event.rows;
      this.resourceQueryRef.refetch({
        page: currentPage,
        size: event.rows,
        search: this.searchCriteria,
        sort: this.sort
      }).then(() => {
      });
    }, 0);
  }

  /**
   * filter resources
   */
  doFilter(): void {
    this.first = 0;
    clearTimeout(this.filterTimer);
    this.filterTimer = setTimeout(() => {
      this.resourceQueryRef.refetch({
        search: this.searchCriteria,
        page: this.first / this.pageSize,
        size: this.pageSize,
        sort: this.sort
      }).then(() => {
      });
    }, 300);
  }


  /**
   * open category dialog
   */
  openCategoryManager(event): void {
    event.stopPropagation();
    this.dialogService.open(CategoryManagerComponent, {
      contentStyle: { 'min-width': '50%' },
      focusOnShow: false,
      modal: true,
      draggable: false,
      closable: true,
      closeAriaLabel:$localize`:@@media-upload.categoryManager.header.closeButton:Close`,
      focusTrap: true,
      data: {
        delegate: this
      },
      header: $localize`:@@media-upload.categoryManager.header:Categories`,
    }).onClose.subscribe((result) => {
      this.doFilter();
      this.loadResourceCategories();
    });
  }

  onEditResource(resource: Resource): void {
    this.dialogControl.editResourceDialog = true;
    this.dialogControl.context = { ...resource };
  }

  // onEditResourceSave(resource: Resource): void {
  //   if (resource.resourceCategory) {
  //     resource.resourceCategoryId = resource.resourceCategory.id;
  //   }

  //   const request = { notes: resource.notes, resourceCategoryId: resource.resourceCategoryId, status: resource.status } as BasicResourceUpdateInput;
  //   this.apollo.mutate<Mutation>({
  //     mutation: this.UPDATE_RESOURCE_MUTATION,
  //     variables: { id: resource.id, resource: request }
  //   }).subscribe(result => {
  //     if (result.data.updateResource) {
  //       this.messageService.add({ severity: 'success', text: $localize`:@@media-library.resource-updated.message:Ressource saved` } as Message);
  //       const index = this.resources.findIndex((r) => r.id === resource.id);
  //       if (index > -1) {
  //         this.resources[index] = resource;
  //         this.resources = [...this.resources];
  //       }
  //       this.dialogControl.editResourceDialog = false;
  //       const event = new CustomEvent('resource-updated');
  //       window.dispatchEvent(event);
  //     }
  //   });
  // }
  /**
   * deletes multiple resources
   */
  onDeleteMultipleResources(): void {
    const selectedRsrs = this.selectedResource as Resource[];
    this.confirmationService.confirm({
      header: $localize`:@@media-library.delete-multi.header:Delete ${selectedRsrs.length} resources`,
      key:'mediathekConfirm',
      message: $localize`:@@media-library.delete-multi.message:Do you really want to delete the ${selectedRsrs.length} selected resources ?`,
      acceptLabel: $localize`:@@media-library.delete-multi.buttons.yes:Delete`,
      rejectLabel: $localize`:@@media-library.delete-multi.buttons.cancel:Cancel`,
      acceptButtonStyleClass: 'p-button-danger',
      defaultFocus: 'none',
      accept: () => {
        this.apollo.query<Mutation>({
          query: this.DELETE_MULTIRESOURCE_MUTATION,
          variables: { ids: selectedRsrs.map((r) => r.id) }
        }).subscribe((result) => {
          if (!result.error) {
            const message = $localize`:@@media-library.delete-resources-ok:Resources deleted`;
            this.messageService.add({ severity: 'success', detail: message });
            this.selectedResource = [];
            this.resourceQueryRef.refetch({
              search: this.searchCriteria,
              page: this.first / this.pageSize,
              size: this.pageSize,
              sort: { property: 'lastModificationDate', direction: Direction.Desc }
            }).then(() => {
            });
          }
          const event = new CustomEvent('resource-deleted');
          window.dispatchEvent(event);
          this.updateUsageStatistics();
        });
      }
    });
  }
  /**
   * deletes resource from server
   * @param resource
   */
  onDeleteResource(resource: Resource): void {
    this.confirmationService.confirm({
      header: $localize`:@@media-library.delete.header:Delete resource`,
      message: $localize`:@@media-library.delete.message:Do you really want to delete resource ${resource.title} ?`,
      acceptLabel: $localize`:@@media-library.delete.buttons.yes:Delete`,
      rejectLabel: $localize`:@@media-library.delete.buttons.cancel:Cancel`,
      acceptButtonStyleClass: 'p-button-danger',
      defaultFocus: 'none',
      accept: () => {
        this.apollo.query<Mutation>({
          query: this.DELETE_RESOURCE_MUTATION,
          variables: { id: resource.id }
        }).subscribe((result) => {
          if (!result.error) {
            const message = $localize`:@@media-library.delete-ok:Resource deleted`;
            this.messageService.add({ severity: 'success', detail: message });
            const index = this.resources.indexOf(resource);
            this.resources.splice(index, 1);
            this.resources = [...this.resources];
            const event = new CustomEvent('resource-deleted');
            window.dispatchEvent(event);
            this.updateUsageStatistics();
          }
        });
      }
    });
  }

  isDeleteDisabled(selected: Resource[] | Resource): boolean {
    if (selected == null || (Array.isArray(selected) && selected.length == 0)) {
      return true;
    }
    return false;
  }

  getUsagePercentage(): number {
    return Math.round(this.usageStatistics.storageUsedPercentage)
  }

  // row bearbeitung funktionen
  onRowEditInit(event: Event, resource: Resource): void {
    event.stopPropagation();
    this.clonedEntries[resource.id] = { ...resource };
  }

  onRowEditSave(event: Event, resource: Resource): void {
    event.stopPropagation();
    delete this.clonedEntries[resource.id];

    if (resource.resourceCategory) {
      resource.resourceCategoryId = resource.resourceCategory.id;
    }
    const request = { notes: resource.notes, resourceCategoryId: resource.resourceCategoryId, status: resource.status, altText: resource.altText } as BasicResourceUpdateInput;
    this.apollo.mutate<Mutation>({
      mutation: this.UPDATE_RESOURCE_MUTATION,
      variables: { id: resource.id, resource: request }
    }).subscribe(result => {
      if (result.data.updateResource) {
        this.messageService.add({ severity: 'success', summary: $localize`:@@media-library.resource-updated.message:Ressource saved` } as ToastMessageOptions);
        const index = this.resources.findIndex((r) => r.id === resource.id);
        if (index > -1) {
          this.resources[index] = resource;
          this.resources = [...this.resources];
        }
        this.dialogControl.editResourceDialog = false;
        const event = new CustomEvent('resource-updated');
        window.dispatchEvent(event);
      }
    });
  }

  onRowEditCancel(event: Event, resource: Resource, index: number): void {
    event.stopPropagation();
    this.resources[index] = this.clonedEntries[resource.id];
    delete this.clonedEntries[resource.id];
    this.resources = [...this.resources];
  }

   /**
     * Bugfix https://github.com/primefaces/primeng/issues/6982
     * @param event
     * @returns
     */
   checkKeyUpCheck(event: KeyboardEvent) {
    if (event.key !== 'Enter' && event.key !== 'Escape') {
      event.stopPropagation();
    }
    return;
  }
    resourceIsActive(resource: Resource):boolean{
      return resource.status == ResourceStatus.Active;
    }

    onModalShow(modal: string){
      let elem = document.getElementById(modal).querySelectorAll(".p-dialog-close-button").item(0) as HTMLElement;
      elem.focus();
    }
}
